/**
 * The "Anpassa text" document view: walks the tracked, resolved IR and
 * renders it as React with the preview theme's class names (inside a
 * shadow root, so it looks like the preview and nothing leaks either way).
 * Every node the replay stamped with an address gets hover controls that
 * turn into action-log entries.
 *
 * Two views over the same tracked document (ADR-0102 decision 1):
 * - `clean` (default): the adapted document as it will read — removed
 *   nodes are not shown, adapted and inserted passages carry a subtle
 *   marker ("Anpassad text");
 * - `track`: "Visa ändringar mot standard" — removed nodes struck through
 *   with "Återställ", inserted ones underlined, edited text as before/after.
 *
 * Locked (statutory) nodes and signatures offer no edit / remove controls
 * (decision 7), only a lock notice.
 */

import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { createHtmlRenderer, formatNodeAddress, isRemovable, numberAffixes, toTokenText } from '@hyranai/regta-document-ir';
import type {
  ActionResult,
  AdaptationPart,
  AdaptationSession,
  AvailableField,
  BlockNode,
  CustomFieldType,
  EditMark,
  InlineNode,
  LogAnchor,
  ResolvedDocumentIR,
  SectionNode,
} from '@hyranai/regta-document-ir';
import type { FinetuneAction } from '../../../api.js';
import type { ChangeCommentCount } from '../../../components/beta-review/review-model.js';
import { finetuneDocumentCss } from './editor-css.js';
import {
  anchorOf,
  changeSeq,
  cleanMark,
  instanceAnchorOf,
  instanceRuns,
  isLocked,
  nodeActions,
  nodeNoun,
  removeAction,
  restoreAnchors,
  undoActionInput,
  valuesIn,
  type NodeActions,
} from './model.js';
import {
  chipLabels,
  droppedReferences,
  pickerFields,
  usedPending,
  type PendingField,
  type PickerField,
} from './adapt.js';
import { FieldPicker, TokenEditor, type TokenEditorHandle } from './TokenEditor.js';
import type { ChipInfo } from './token-dom.js';

type Target = { anchor: LogAnchor; fingerprint: string };
type MenuItem = { label: string; danger?: boolean; run: () => void };
type Ctx = { insideGhost: boolean; editable: boolean; menu: MenuItem[]; parentLocked: boolean; parentMarked?: boolean };

export type DocumentView = 'clean' | 'track';

export const LOCK_NOTICE = 'Lagstadgad text – kan inte ändras';
const ADAPTED_TITLE = 'Anpassad text';
const INSERTED_TITLE = 'Tillagd text';

type EditState =
  | { key: string; kind: 'text' }
  | { key: string; kind: 'paragraph'; address: string; position: 'after' | 'append' }
  | { key: string; kind: 'clause'; address: string }
  | { key: string; kind: 'row'; address: string }
  | { key: string; kind: 'insertRow'; address: string; position: 'before' | 'after' }
  | { key: string; kind: 'label'; address: string };

export type DocumentFocus = { address: string; n: number };

/**
 * The adaptation session's side of editing (ADR-0102 addendum): text goes
 * through the framework's action builders as token text, so references
 * stay live and own fields can be created inline.
 */
export type AdaptApi = {
  session: AdaptationSession;
  /** What a chip may reference. */
  fields: readonly AvailableField[];
  /** A new own field for an open editor, or a Swedish error. */
  newField: (label: string, type: CustomFieldType, pending: readonly PendingField[]) => PendingField | string;
  /**
   * Builds (defining `pending` first) and appends; resolves `true` when saved
   * or nothing changed. A non-empty `comment` is saved on the change.
   */
  run: (build: (s: AdaptationSession) => ActionResult, pending?: readonly PendingField[], comment?: string) => Promise<boolean>;
};

const OWN_SECTION_KEY = (address: string) => `section:${address}`;

export function IrEditableDocument({
  document: doc,
  editable,
  busy,
  blockKeys,
  focus,
  view = 'clean',
  undoTarget = (seq) => seq,
  onActions,
  adapt,
  hideAdaptations = false,
  onValueClick,
  onCommentNode,
  changeCommentLabel = null,
  commentCounts,
  onShowChangeComments,
}: {
  document: ResolvedDocumentIR;
  /** `clean` hides removals and marks adaptations subtly; `track` shows the diff. */
  view?: DocumentView;
  editable: boolean;
  busy: boolean;
  blockKeys: ReadonlyMap<string, string>;
  focus: DocumentFocus | null;
  /**
   * The entry an "Ångra" / "Återställ" on a node marked with `seq` reverts
   * (the group head; never a superseded version), or `null` for none.
   */
  undoTarget?: (seq: number) => number | null;
  /** Appends the actions; resolves `true` when they were saved. */
  onActions: (actions: FinetuneAction[]) => Promise<boolean>;
  /** Required for text editing and inserts; without it the document only offers remove / undo. */
  adapt?: AdaptApi | null;
  /** Review (ADR-0102 addendum 2): the author's adaptations read as plain text unless shown ("Visa malländringar"). */
  hideAdaptations?: boolean;
  /** Field navigation: a click on a field-bound value (chip or ticked box). */
  onValueClick?: (key: string, rect: DOMRect) => void;
  /** Adds "Kommentera" to a node's tools; `changeSeq` is the node's change, when it has one. */
  onCommentNode?: (address: string, changeSeq: number | null) => void;
  /** Label of the optional comment in edit forms ("Motivering (valfritt)"); `null` hides it. */
  changeCommentLabel?: string | null;
  /** Comment counts per change seq: a changed node with comments shows a badge. */
  commentCounts?: ReadonlyMap<number, ChangeCommentCount>;
  /** A click on a node's comment badge. */
  onShowChangeComments?: (seq: number) => void;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [mount, setMount] = useState<HTMLElement | null>(null);
  const hot = useRef<Element | null>(null);
  const [editing, setEditing] = useState<EditState | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const root = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    root.replaceChildren();
    const style = window.document.createElement('style');
    style.textContent = finetuneDocumentCss;
    const container = window.document.createElement('div');
    root.append(style, container);
    setMount(container);
  }, []);

  useEffect(() => {
    if (!editable) setEditing(null);
  }, [editable]);

  useEffect(() => {
    if (!focus || !mount) return;
    const el = mount.querySelector<HTMLElement>(`[data-ft-address="${CSS.escape(focus.address)}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('ft-flash');
    const t = setTimeout(() => el.classList.remove('ft-flash'), 1200);
    return () => clearTimeout(t);
  }, [focus, mount]);

  const affixes = numberAffixes(doc.numberStyle);
  const runIn = affixes.suffix === '.';
  const html = useMemo(
    () => createHtmlRenderer({ sectionSymbol: affixes.prefix, sectionSuffix: affixes.suffix }),
    [affixes.prefix, affixes.suffix],
  );
  const restoreHere = useMemo(() => restoreAnchors(doc), [doc]);
  const track = view === 'track';

  const submit = async (actions: Array<FinetuneAction | null>) => {
    const list = actions.filter((a): a is FinetuneAction => a !== null);
    if (list.length === 0) return;
    setMenuFor(null);
    if (await onActions(list)) setEditing(null);
  };
  const runBuild = async (build: (s: AdaptationSession) => ActionResult, pending?: readonly PendingField[], comment?: string) => {
    if (!adapt) return;
    setMenuFor(null);
    if (await adapt.run(build, pending, comment)) setEditing(null);
  };
  const commentLabel = changeCommentLabel;

  const badge = (seq: number | null, inline = false): ReactNode => {
    const count = seq != null ? commentCounts?.get(seq) : undefined;
    if (seq == null || !count) return null;
    const label = `${count.total} ${count.total === 1 ? 'kommentar' : 'kommentarer'}${count.open < count.total ? ` (${count.open} öppna)` : ''} – visa ändringen`;
    return (
      <button
        type="button"
        className={'ft-cbadge' + (inline ? ' ft-cbadge--inline' : '') + (count.open === 0 ? ' ft-cbadge--resolved' : '')}
        title={label}
        aria-label={label}
        onClick={(e) => {
          e.stopPropagation();
          onShowChangeComments?.(seq);
        }}
      >
        <BubbleGlyph />
        {count.total}
      </button>
    );
  };
  const canWrite = editable && !!adapt;

  const inlineHtml = (content: InlineNode[]) => html.inlines(content);
  const editedHtml = (node: { content: InlineNode[]; edit?: BlockNode['edit'] }) => {
    const e = node.edit;
    if (!track || e?.change !== 'edited' || !e.before) return inlineHtml(node.content);
    const sug = e.suggestion ? ` data-suggestion="${e.suggestion}"` : '';
    return `<del class="${changeClass('del', e)}"${sug}>${inlineHtml(e.before)}</del><ins class="${changeClass('ins', e)}"${sug}>${inlineHtml(node.content)}</ins>`;
  };

  /* -------------------------- controls -------------------------- */

  const toolbar = (key: string, node: BlockNode, actions: NodeActions, ctx: Ctx): ReactNode => {
    const at = anchorOf(node);
    const address = node.edit?.address;
    const buttons: ReactNode[] = [];
    if (actions.lockNotice) {
      buttons.push(
        <span key="lock" className="ft-lock" title="Texten är lagstadgad och kan inte ändras eller tas bort.">
          <LockGlyph /> {LOCK_NOTICE}
        </span>,
      );
    }
    if (actions.edit && canWrite) {
      buttons.push(
        <button key="edit" type="button" className="ft-btn" disabled={busy} onClick={() => setEditing({ key, kind: 'text' })}>
          Redigera
        </button>,
      );
    }
    if (actions.insertParagraph && at && address && canWrite) {
      buttons.push(
        <button
          key="ins-p"
          type="button"
          className="ft-btn"
          disabled={busy}
          onClick={() => setEditing({ key, kind: 'paragraph', address, position: 'after' })}
        >
          + Stycke
        </button>,
      );
    }
    if (actions.appendParagraph && at && address && canWrite) {
      buttons.push(
        <button
          key="app-p"
          type="button"
          className="ft-btn"
          disabled={busy}
          onClick={() => setEditing({ key, kind: 'paragraph', address, position: 'append' })}
        >
          + Stycke sist
        </button>,
      );
    }
    if (actions.insertClause && at && address && canWrite) {
      buttons.push(
        <button
          key="ins-c"
          type="button"
          className="ft-btn"
          disabled={busy}
          title="Lägg till ett eget avsnitt efter det här"
          onClick={() => setEditing({ key: OWN_SECTION_KEY(address), kind: 'clause', address })}
        >
          + Eget avsnitt
        </button>,
      );
    }
    const undoSeq = actions.undoSeq != null ? undoTarget(actions.undoSeq) : null;
    if (undoSeq != null) {
      const seq = undoSeq;
      buttons.push(
        <button
          key="undo"
          type="button"
          className="ft-btn"
          disabled={busy}
          title={`Ångra ändring #${seq}`}
          onClick={() => void submit([undoActionInput(seq)])}
        >
          Ångra
        </button>,
      );
    }
    if (actions.remove && at) {
      buttons.push(
        <button
          key="rm"
          type="button"
          className="ft-btn ft-btn--danger"
          disabled={busy}
          title={`Ta bort ${nodeNoun(node)}`}
          onClick={() => void submit([removeAction(at)])}
        >
          Ta bort
        </button>,
      );
    }
    if (onCommentNode && address && !ctx.insideGhost) {
      const seq = changeSeq(node.edit);
      buttons.push(
        <button
          key="comment"
          type="button"
          className="ft-btn"
          title={seq != null ? 'Kommentera ändringen' : 'Kommentera stycket'}
          onClick={() => onCommentNode(address, seq)}
        >
          Kommentera
        </button>,
      );
    }
    if (ctx.menu.length > 0) {
      buttons.push(
        <button
          key="more"
          type="button"
          className="ft-btn"
          aria-haspopup="menu"
          aria-expanded={menuFor === key}
          disabled={busy}
          onClick={() => setMenuFor((m) => (m === key ? null : key))}
        >
          ⋯
        </button>,
      );
    }
    if (buttons.length === 0) return null;
    return (
      <div className="ft-tools" data-open={menuFor === key ? '' : undefined}>
        {buttons}
        {menuFor === key && (
          <div className="ft-menu" role="menu">
            {ctx.menu.map((m) => (
              <button
                key={m.label}
                type="button"
                role="menuitem"
                className={'ft-btn' + (m.danger ? ' ft-btn--danger' : '')}
                disabled={busy}
                onClick={m.run}
              >
                {m.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const insertEditor = (key: string): ReactNode => {
    if (!adapt || !editing || editing.key !== key || (editing.kind !== 'paragraph' && editing.kind !== 'clause')) return null;
    if (editing.kind === 'paragraph') {
      const { address, position } = editing;
      return (
        <InsertParagraphEditor
          adapt={adapt}
          busy={busy}
          commentLabel={commentLabel}
          onCancel={() => setEditing(null)}
          onSave={(text, pending, comment) => void runBuild((s) => s.actions.insertParagraph(address, position, text), pending, comment)}
        />
      );
    }
    const { address } = editing;
    return (
      <OwnSectionEditor
        adapt={adapt}
        busy={busy}
        commentLabel={commentLabel}
        onCancel={() => setEditing(null)}
        onSave={(title, body, pending, comment) => void runBuild((s) => s.actions.insertSection(address, 'after', title, body), pending, comment)}
      />
    );
  };

  /** Wraps a rendered node in its hover target, toolbar and inline editors. */
  const shell = (key: string, node: BlockNode, ctx: Ctx, content: (editingText: boolean) => ReactNode): ReactNode => {
    const address = node.edit?.address;
    const actions = nodeActions(node, {
      editable: ctx.editable,
      insideGhost: ctx.insideGhost,
      restoreHere: restoreHere.has(node),
      parentLocked: ctx.parentLocked,
    });
    const mark = ctx.insideGhost ? null : cleanMark(node);
    if (!track && mark === 'hidden') return null;
    if (node.edit?.change === 'removed') {
      const seq = actions.restoreSeq != null && undoTarget(actions.restoreSeq) === actions.restoreSeq ? actions.restoreSeq : null;
      return (
        <div key={key} className="ft-node" data-ft-address={address} data-ft-change="removed">
          <del className={changeClass('del', node.edit, true)} {...layerProps(node.edit)}>{content(false)}</del>
          {restoreHere.has(node) && badge(changeSeq(node.edit))}
          {seq != null && (
            <button type="button" className="ft-restore" disabled={busy} onClick={() => void submit([undoActionInput(seq, 'restore')])}>
              Återställ
            </button>
          )}
        </div>
      );
    }
    const editingText = editing?.key === key && editing.kind === 'text' && actions.edit;
    const body =
      track && node.edit?.change === 'inserted' ? (
        <ins className={changeClass('ins', node.edit, true)} {...layerProps(node.edit)}>
          {content(editingText)}
        </ins>
      ) : (
        content(editingText)
      );
    // Clean view: a thin rule beside adapted / inserted passages — not on
    // their children, which sit inside the marked node already.
    // An edited section title is marked on the heading itself (sectionNode).
    const titleOnly = node.type === 'section' && node.edit?.change === 'edited';
    const marked = !track && (mark === 'adapted' || mark === 'inserted') && !ctx.parentMarked && !titleOnly;
    const markProps = marked
      ? { 'data-ft-mark': mark as string, title: mark === 'inserted' ? INSERTED_TITLE : ADAPTED_TITLE }
      : {};
    const nodeBadge = ctx.insideGhost || ctx.parentMarked ? null : badge(changeSeq(node.edit));
    if (!ctx.editable || ctx.insideGhost || !address) {
      return (
        <div key={key} data-ft-address={address} data-ft-badge={nodeBadge ? '' : undefined} {...markProps}>
          {body}
          {nodeBadge}
        </div>
      );
    }
    return (
      <div
        key={key}
        className="ft-node"
        data-ft-address={address}
        data-ft-change={node.edit?.change}
        data-ft-locked={actions.lockNotice ? '' : undefined}
        data-editing={editingText ? '' : undefined}
        {...markProps}
      >
        {body}
        {nodeBadge}
        {!editingText && toolbar(key, node, actions, ctx)}
        {insertEditor(key)}
        {insertEditor(OWN_SECTION_KEY(address))}
      </div>
    );
  };

  const textEditorFor = (node: BlockNode): ReactNode => {
    const address = node.edit?.address;
    if (!adapt || !address) return null;
    if (node.type === 'section') {
      return (
        <TitleEditor
          initial={node.title ?? ''}
          busy={busy}
          commentLabel={commentLabel}
          onCancel={() => setEditing(null)}
          onSave={(title, comment) => void runBuild((s) => s.actions.editText(address, title), undefined, comment)}
        />
      );
    }
    if (node.type !== 'paragraph' && node.type !== 'heading') return null;
    return (
      <TextEditor
        adapt={adapt}
        initial={adapt.session.nodeAt(address)?.text ?? toTokenText(node.content)}
        original={node.content}
        busy={busy}
        commentLabel={commentLabel}
        onCancel={() => setEditing(null)}
        onSave={(text, pending, comment) => void runBuild((s) => s.actions.editText(address, text), pending, comment)}
      />
    );
  };

  /* ---------------------- parts: rows and labels ---------------------- */

  const partAddress = (edit: EditMark | undefined): string | undefined =>
    edit?.address ?? (edit?.change === 'inserted' && edit.patchId ? formatNodeAddress({ fromPatch: edit.patchId }) : undefined);
  const partOf = (edit: EditMark | undefined): AdaptationPart | undefined => {
    const address = partAddress(edit);
    return address && adapt ? adapt.session.partAt(address) : undefined;
  };
  const partActive = (part: AdaptationPart | undefined, ctx: Ctx): part is AdaptationPart =>
    !!part && canWrite && ctx.editable && !ctx.insideGhost && part.change !== 'removed';

  const partTools = (key: string, part: AdaptationPart): ReactNode => {
    const buttons: ReactNode[] = [];
    const addMenu = `${key}:add`;
    if (part.kind === 'row') {
      if (part.ops.editRow) {
        buttons.push(
          <button key="edit" type="button" className="ft-btn" disabled={busy} onClick={() => setEditing({ key, kind: 'row', address: part.address })}>
            Ändra rad
          </button>,
        );
      }
      if (part.ops.insertRowBefore || part.ops.insertRowAfter) {
        buttons.push(
          <button
            key="add"
            type="button"
            className="ft-btn"
            aria-haspopup="menu"
            aria-expanded={menuFor === addMenu}
            disabled={busy}
            onClick={() => setMenuFor((m) => (m === addMenu ? null : addMenu))}
          >
            Lägg till rad
          </button>,
        );
      }
    } else if (part.ops.editLabel) {
      buttons.push(
        <button key="edit" type="button" className="ft-btn" disabled={busy} onClick={() => setEditing({ key, kind: 'label', address: part.address })}>
          Redigera
        </button>,
      );
    }
    if (part.undoSeq != null) {
      const seq = part.undoSeq;
      buttons.push(
        <button key="undo" type="button" className="ft-btn" disabled={busy} title={`Ångra ändring #${seq}`} onClick={() => void submit([undoActionInput(seq)])}>
          Ångra
        </button>,
      );
    }
    if (onCommentNode) {
      const seq = part.change && part.change !== 'removed' ? part.seq : null;
      buttons.push(
        <button
          key="comment"
          type="button"
          className="ft-btn"
          title={seq != null ? 'Kommentera ändringen' : part.kind === 'row' ? 'Kommentera raden' : 'Kommentera texten'}
          onClick={() => onCommentNode(part.address, seq)}
        >
          Kommentera
        </button>,
      );
    }
    if (part.kind === 'row' && part.ops.removeRow) {
      buttons.push(
        <button
          key="rm"
          type="button"
          className="ft-btn ft-btn--danger"
          disabled={busy}
          onClick={() => void runBuild((s) => s.actions.removeRow(part.address))}
        >
          Ta bort rad
        </button>,
      );
    }
    if (part.locked && part.kind === 'row' && buttons.length === 0) {
      buttons.push(
        <span key="lock" className="ft-lock" title="Raden är lagstadgad och kan inte ändras eller tas bort.">
          <LockGlyph /> {LOCK_NOTICE}
        </span>,
      );
    }
    if (buttons.length === 0) return null;
    return (
      <div className="ft-tools" data-open={menuFor === addMenu ? '' : undefined}>
        {buttons}
        {menuFor === addMenu && (
          <div className="ft-menu" role="menu">
            {(['before', 'after'] as const)
              .filter((position) => (position === 'before' ? part.ops.insertRowBefore : part.ops.insertRowAfter))
              .map((position) => (
                <button
                  key={position}
                  type="button"
                  role="menuitem"
                  className="ft-btn"
                  disabled={busy}
                  onClick={() => {
                    setMenuFor(null);
                    setEditing({ key, kind: 'insertRow', address: part.address, position });
                  }}
                >
                  {position === 'before' ? 'Ny rad ovanför' : 'Ny rad nedanför'}
                </button>
              ))}
          </div>
        )}
      </div>
    );
  };

  /** An editable label (option text, column header, caption): its text, hover tools and inline editor. */
  const labelNode = (key: string, content: InlineNode[], edit: EditMark | undefined, ctx: Ctx): ReactNode => {
    const part = partOf(edit);
    const html = { __html: editedHtml({ content, edit }) };
    const labelBadge = ctx.insideGhost ? null : badge(changeSeq(edit), true);
    if (!partActive(part, ctx)) {
      return (
        <>
          <span dangerouslySetInnerHTML={html} />
          {labelBadge}
        </>
      );
    }
    if (adapt && editing?.key === key && editing.kind === 'label') {
      return (
        <LabelEditor
          adapt={adapt}
          initial={part.text ?? toTokenText(content)}
          original={content}
          busy={busy}
          commentLabel={commentLabel}
          onCancel={() => setEditing(null)}
          onSave={(text, pending, comment) => void runBuild((s) => s.actions.editLabel(part.address, text), pending, comment)}
        />
      );
    }
    const marked = !track && part.change === 'adapted' && !ctx.parentMarked;
    return (
      <span
        className="ft-node ft-label"
        data-ft-address={part.address}
        {...(marked ? { 'data-ft-mark': 'adapted', title: ADAPTED_TITLE } : {})}
      >
        <span dangerouslySetInnerHTML={html} />
        {labelBadge}
        {partTools(key, part)}
      </span>
    );
  };

  /** One detail-list row: label + value, row tools, row editors (ADR-0102 addendum 3). */
  const detailRow = (item: { term: InlineNode[]; description: BlockNode[]; edit?: EditMark }, depth: number, rowKey: string, ctx: Ctx): ReactNode => {
    const e = item.edit;
    const removed = e?.change === 'removed';
    if (removed && !track) return null;
    const part = partOf(e);
    const active = partActive(part, ctx);
    const rowCtx: Ctx = removed ? { ...ctx, insideGhost: true } : ctx;
    const ghostCtx: Ctx = { ...ctx, insideGhost: true, editable: false };
    const term = { __html: editedHtml({ content: item.term, edit: e }) };
    const value =
      track && e?.change === 'edited' && e.beforeDescription ? (
        <>
          <del className={changeClass('del', e, true)} {...layerProps(e)}>{blocks(e.beforeDescription, depth, ghostCtx, `${rowKey}:before`)}</del>
          <ins className={changeClass('ins', e, true)} {...layerProps(e)}>{blocks(item.description, depth, rowCtx, rowKey)}</ins>
        </>
      ) : (
        blocks(item.description, depth, rowCtx, rowKey)
      );
    const editingRow = active && adapt && editing?.key === rowKey && editing.kind === 'row';
    const inserting = active && adapt && editing?.key === rowKey && editing.kind === 'insertRow' ? editing : null;
    const mark = !track && !ctx.parentMarked && (e?.change === 'edited' || e?.change === 'inserted') ? (e.change === 'inserted' ? 'inserted' : 'adapted') : null;
    const className = [
      'rd-details__row',
      track && removed ? changeClass('del', e, true) : '',
      track && e?.change === 'inserted' ? changeClass('ins', e, true) : '',
      active ? 'ft-node ft-row' : '',
    ]
      .filter(Boolean)
      .join(' ');
    const restoreSeq = removed && part?.restoreSeq != null && editable ? part.restoreSeq : null;
    const original = [...item.term, ...item.description.flatMap((n) => (n.type === 'paragraph' ? n.content : []))];
    const rowEditor = (
      initialTerm: string,
      initialValue: string,
      valueEditable: boolean,
      saveLabel: string,
      onSave: (t: string, v: string | undefined, pending: PendingField[], comment: string) => void,
    ) =>
      adapt ? (
        <RowEditor
          adapt={adapt}
          initialTerm={initialTerm}
          initialValue={initialValue}
          valueEditable={valueEditable}
          original={original}
          busy={busy}
          saveLabel={saveLabel}
          commentLabel={commentLabel}
          onCancel={() => setEditing(null)}
          onSave={onSave}
        />
      ) : null;
    return (
      <Fragment key={rowKey}>
        {inserting?.position === 'before' && (
          <div className="ft-row-editor">
            {rowEditor('', '', true, 'Lägg till', (t, v, pending, comment) =>
              void runBuild((s) => s.actions.insertRow(inserting.address, 'before', t, v ?? ''), pending, comment),
            )}
          </div>
        )}
        <div
          className={className}
          data-ft-address={part?.address}
          data-ft-change={e?.change}
          {...(mark ? { 'data-ft-mark': mark, title: mark === 'inserted' ? INSERTED_TITLE : ADAPTED_TITLE } : {})}
        >
          {editingRow && part ? (
            <div className="ft-row-editor">
              {rowEditor(part.text ?? toTokenText(item.term), part.description ?? '', part.descriptionEditable === true, 'Spara', (t, v, pending, comment) =>
                void runBuild((s) => s.actions.editRow(part.address, t, v), pending, comment),
              )}
            </div>
          ) : (
            <>
              <dt className="rd-details__term" dangerouslySetInnerHTML={term} />
              <dd className="rd-details__value">{value}</dd>
              {active && partTools(rowKey, part)}
            </>
          )}
          {restoreSeq != null && (
            <button type="button" className="ft-restore" disabled={busy} onClick={() => void submit([undoActionInput(restoreSeq, 'restore')])}>
              Återställ
            </button>
          )}
          {!ctx.insideGhost && !ctx.parentMarked && badge(changeSeq(e))}
        </div>
        {inserting?.position === 'after' && (
          <div className="ft-row-editor">
            {rowEditor('', '', true, 'Lägg till', (t, v, pending, comment) =>
              void runBuild((s) => s.actions.insertRow(inserting.address, 'after', t, v ?? ''), pending, comment),
            )}
          </div>
        )}
      </Fragment>
    );
  };

  /* -------------------------- blocks -------------------------- */

  const childCtx = (node: BlockNode, ctx: Ctx): Ctx => {
    const insideGhost = ctx.insideGhost || node.edit?.change === 'removed';
    const at = anchorOf(node);
    const container = node.type !== 'paragraph' && node.type !== 'heading';
    const locked = ctx.parentLocked || isLocked(node);
    const extra: MenuItem[] =
      container && at && ctx.editable && !insideGhost && !locked && isRemovable([node])
        ? [{ label: `Ta bort ${nodeNoun(node)}`, danger: true, run: () => void submit([removeAction(at)]) }]
        : [];
    const mark = cleanMark(node);
    const parentMarked = !!ctx.parentMarked || mark === 'inserted';
    return { ...ctx, insideGhost, parentLocked: locked, parentMarked, menu: [...extra, ...ctx.menu] };
  };

  const sectionNode = (node: SectionNode, depth: number, key: string, ctx: Ctx): ReactNode =>
    shell(key, node, ctx, (editingText) => {
      const level = Math.min(depth + 1, 6);
      const H = `h${level}` as 'h2';
      const e = node.edit;
      const title =
        track && e?.change === 'edited' && e.beforeTitle != null ? (
          <>
            <del className={changeClass('del', e)} {...layerProps(e)}>{e.beforeTitle}</del>
            <ins className={changeClass('ins', e)} {...layerProps(e)}>{node.title}</ins>
          </>
        ) : (
          node.title
        );
      const inner = childCtx(node, ctx);
      return (
        <section
          className={['rd-section', `rd-section--depth-${depth}`, runIn && depth === 1 ? 'rd-section--run-in' : ''].join(' ').trim()}
          data-ref={node.ref}
          data-variant={node.variant}
        >
          {editingText ? (
            textEditorFor(node)
          ) : node.title ? (
            <H
              className={'rd-section__heading' + (runIn ? ' rd-section__heading--run-in' : '')}
              {...(!track && e?.change === 'edited' && !ctx.parentMarked
                ? { 'data-ft-mark': 'adapted', title: ADAPTED_TITLE }
                : {})}
            >
              {node.number ? (
                <span className="rd-section__number">
                  {depth === 1 ? `${affixes.prefix}${node.number}${affixes.suffix}` : node.number}
                </span>
              ) : null}
              <span className="rd-section__title">{title}</span>
            </H>
          ) : null}
          <div className="rd-section__body">{blocks(node.content, depth + 1, inner, key)}</div>
        </section>
      );
    });

  const block = (node: BlockNode, depth: number, key: string, ctx: Ctx): ReactNode => {
    switch (node.type) {
      case 'section':
        return sectionNode(node, depth, key, ctx);
      case 'paragraph':
        return shell(key, node, ctx, (editingText) =>
          editingText ? (
            textEditorFor(node)
          ) : (
            <p
              className={[
                'rd-p',
                node.variant && node.variant !== 'body' ? `rd-p--${node.variant}` : '',
                node.align && node.align !== 'left' ? `rd-p--${node.align}` : '',
              ]
                .join(' ')
                .trim()}
              dangerouslySetInnerHTML={{ __html: editedHtml(node) }}
            />
          ),
        );
      case 'heading': {
        const H = `h${node.level}` as 'h1';
        return shell(key, node, ctx, (editingText) =>
          editingText ? (
            textEditorFor(node)
          ) : (
            <H
              className={'rd-heading' + (node.align === 'center' ? ' rd-heading--center' : '')}
              dangerouslySetInnerHTML={{ __html: editedHtml(node) }}
            />
          ),
        );
      }
      case 'list': {
        const L = node.ordered ? 'ol' : 'ul';
        const inner = childCtx(node, ctx);
        return shell(key, node, ctx, () => (
          <L className="rd-list">
            {node.items.map((item, i) => (
              <li key={i} className="rd-list__item">
                {blocks(item.content, depth, inner, `${key}.${i}`)}
              </li>
            ))}
          </L>
        ));
      }
      case 'table': {
        const inner = childCtx(node, ctx);
        const hasHead = node.columns.some((c) => c.header?.length);
        return shell(key, node, ctx, () => (
          <table className={'rd-table' + (node.variant ? ` rd-table--${node.variant}` : '')} style={{ width: '100%' }}>
            {node.caption?.length ? (
              <caption className="rd-table__caption">{labelNode(`${key}:caption`, node.caption, node.captionEdit, inner)}</caption>
            ) : null}
            {node.columns.some((c) => c.width) && (
              <colgroup>
                {node.columns.map((c, i) => (
                  <col key={i} style={c.width ? { width: `${c.width}%` } : undefined} />
                ))}
              </colgroup>
            )}
            {hasHead && (
              <thead>
                <tr>
                  {node.columns.map((c, i) => (
                    <th key={i} className="rd-table__th" data-align={c.align}>
                      {labelNode(`${key}:th${i}`, c.header ?? [], c.edit, inner)}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {node.rows.map((r, ri) => (
                <tr key={ri} className={'rd-table__tr' + (r.variant === 'total' ? ' rd-table__tr--total' : '')}>
                  {r.cells.map((c, ci) => (
                    <td
                      key={ci}
                      className="rd-table__td"
                      colSpan={c.colSpan && c.colSpan > 1 ? c.colSpan : undefined}
                      data-align={node.columns[ci]?.align}
                    >
                      {blocks(c.content, depth, inner, `${key}.${ri}.${ci}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ));
      }
      case 'detailList': {
        const inner = childCtx(node, ctx);
        return shell(key, node, ctx, () => (
          <dl
            className={[
              'rd-details',
              node.variant === 'boxed' ? 'rd-details--boxed' : '',
              node.columns === 2 ? 'rd-details--cols-2' : '',
            ]
              .join(' ')
              .trim()}
          >
            {node.items.map((item, i) => detailRow(item, depth, `${key}.${i}`, inner))}
          </dl>
        ));
      }
      case 'optionList': {
        const inner = childCtx(node, ctx);
        const bound = node.sourceKey;
        return shell(key, node, ctx, () => (
          <ul
            className={['rd-options', node.select === 'single' ? 'rd-options--single' : '', node.layout === 'inline' ? 'rd-options--inline' : '']
              .join(' ')
              .trim()}
            data-key={bound}
          >
            {node.items.map((item, i) => (
              <li key={i} className={'rd-option' + (item.checked ? ' rd-option--checked' : '')} data-code={item.code}>
                <span
                  className="rd-option__box"
                  aria-hidden="true"
                  data-key={item.sourceKey ?? bound}
                  title={(item.sourceKey ?? bound) ? 'Kryssas i formuläret' : undefined}
                >
                  {item.checked ? '☒' : '☐'}
                </span>
                <span className="rd-option__label">{labelNode(`${key}:o${i}`, item.content, item.edit, inner)}</span>
                {item.children ? <div className="rd-option__children">{blocks(item.children, depth, inner, `${key}.${i}`)}</div> : null}
              </li>
            ))}
          </ul>
        ));
      }
      case 'notice': {
        const inner = childCtx(node, ctx);
        return shell(key, node, ctx, () => (
          <aside className={`rd-notice rd-notice--${node.tone ?? 'info'}`}>
            {node.title ? <p className="rd-notice__title">{node.title}</p> : null}
            {blocks(node.content, depth, inner, key)}
          </aside>
        ));
      }
      case 'group': {
        const inner = childCtx(node, ctx);
        return shell(key, node, ctx, () => (
          <div className={`rd-group rd-group--${node.variant ?? 'plain'}`}>
            {node.title ? <p className="rd-group__title">{node.title}</p> : null}
            {node.variant === 'columns' ? (
              <div className="rd-group__columns">
                {node.content.map((child, i) => (
                  <div key={i} className="rd-group__column">
                    {block(child, depth, `${key}.${i}`, inner)}
                  </div>
                ))}
              </div>
            ) : (
              blocks(node.content, depth, inner, key)
            )}
          </div>
        ));
      }
      default: {
        // Signatures, attachment lists, spacers: printed as the
        // preview prints them; editable only as a whole (remove / insert after).
        const { edit: _edit, ...clean } = node;
        return shell(key, node, ctx, () => (
          <div dangerouslySetInnerHTML={{ __html: html.block(clean as BlockNode, depth) }} />
        ));
      }
    }
  };

  /** A block list; a run of `sideBySide` nodes shares a row, as in the renderer. */
  function blocks(list: readonly BlockNode[], depth: number, ctx: Ctx, keyPrefix: string): ReactNode[] {
    const out: ReactNode[] = [];
    for (let i = 0; i < list.length; ) {
      let end = i + 1;
      while (end < list.length && (list[end] as { sideBySide?: boolean }).sideBySide) end++;
      const run = list.slice(i, end);
      if (run.length === 1) out.push(block(run[0]!, depth, `${keyPrefix}/${i}`, ctx));
      else {
        out.push(
          <div key={`${keyPrefix}/row${i}`} className={`rd-row rd-row--${run.length}`}>
            {run.map((n, j) => (
              <div key={j} className="rd-row__cell">
                {block(n, depth, `${keyPrefix}/${i + j}`, ctx)}
              </div>
            ))}
          </div>,
        );
      }
      i = end;
    }
    return out;
  }

  /** The body: runs of block instances, each offering whole-instance actions in its "⋯" menu. */
  const body = (nodes: readonly BlockNode[], partKey: string): ReactNode[] =>
    instanceRuns(nodes).map((run, r) => {
      const first = run.nodes[0]!;
      const target: Target | null = run.instanceId ? instanceAnchorOf(run.instanceId, first) : null;
      const ghost = run.nodes.every((n) => n.edit?.change === 'removed');
      const menu: MenuItem[] = [];
      if (editable && run.instanceId && target && !ghost) {
        const label = blockKeys.get(run.instanceId) ?? run.instanceId;
        const instanceId = run.instanceId;
        if (canWrite) {
          menu.push({
            label: '+ Eget avsnitt efter blocket',
            run: () => {
              setMenuFor(null);
              setEditing({ key: `instance:${instanceId}`, kind: 'clause', address: formatNodeAddress({ instanceId }) });
            },
          });
        }
        if (isRemovable(run.nodes) && !run.nodes.some(isLocked)) {
          menu.push({ label: `Ta bort hela blocket (${label})`, danger: true, run: () => void submit([removeAction(target)]) });
        }
      }
      const ctx: Ctx = { insideGhost: false, editable, menu, parentLocked: false };
      return (
        <div key={`${partKey}:${r}`} className="ft-instance" data-instance-id={run.instanceId ?? undefined}>
          {blocks(run.nodes, 1, ctx, `${partKey}:${r}`)}
          {run.instanceId && insertEditor(`instance:${run.instanceId}`)}
        </div>
      );
    });

  const readOnly: Ctx = { insideGhost: false, editable: false, menu: [], parentLocked: false };

  const onMouseOver = (e: MouseEvent) => {
    const el = (e.target as Element).closest?.('.ft-node') ?? null;
    if (el === hot.current) return;
    hot.current?.removeAttribute('data-hot');
    el?.setAttribute('data-hot', '');
    hot.current = el;
  };
  const onRootClick = (e: MouseEvent) => {
    if (!onValueClick) return;
    const target = e.target as Element;
    if (target.closest?.('.ft-editor, .ft-tools, .ft-menu')) return;
    const value = target.closest?.<HTMLElement>('[data-key]');
    const key = value?.dataset.key;
    if (!value || !key) return;
    onValueClick(key, value.getBoundingClientRect());
  };
  const onMouseLeave = () => {
    hot.current?.removeAttribute('data-hot');
    hot.current = null;
  };

  // "Lägg till eget avsnitt sist": after the last top-level section (before signatures).
  const endAddress = canWrite
    ? (adapt!.session.nodes.filter((n) => n.nodeType === 'section' && n.depth === 1 && n.ops.insertSection).at(-1)?.address ?? null)
    : null;

  const content = (
    <div className="ft-root" onMouseOver={onMouseOver} onMouseLeave={onMouseLeave} onClick={onRootClick}>
      <div className={'rd-doc' + (hideAdaptations ? ' rd-hide-adaptations' : '') + (onValueClick ? ' ft-values' : '')}>
        {doc.parts.map((part, i) => (
          <div key={part.key}>
            {doc.parts.length > 1 && (
              <div className="ft-part-label">
                Dokument {i + 1} / {doc.parts.length} · {part.title}
              </div>
            )}
            <section className="rd-page" data-part={part.key}>
              {part.letterhead?.length ? (
                <div className="rd-region rd-region--letterhead">{blocks(part.letterhead, 1, readOnly, `${part.key}:lh`)}</div>
              ) : null}
              {body(part.body, part.key)}
              {part.footer?.length ? (
                <div className="rd-region rd-region--footer">{blocks(part.footer, 1, readOnly, `${part.key}:ft`)}</div>
              ) : null}
            </section>
          </div>
        ))}
      </div>
      {endAddress && (
        <div className="ft-end">
          <button
            type="button"
            className="ft-btn ft-btn--add"
            disabled={busy || editing?.key === OWN_SECTION_KEY(endAddress)}
            onClick={() => {
              setMenuFor(null);
              setEditing({ key: OWN_SECTION_KEY(endAddress), kind: 'clause', address: endAddress });
              setTimeout(() => {
                mount
                  ?.querySelector<HTMLElement>(`[data-ft-address="${CSS.escape(endAddress)}"] .ft-editor`)
                  ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
              }, 0);
            }}
          >
            + Lägg till eget avsnitt sist
          </button>
          <span className="ft-editor-hint">Rubrik och text, med fält från formuläret eller egna fält.</span>
        </div>
      )}
    </div>
  );

  return (
    <div ref={hostRef} className="min-h-0 flex-1">
      {mount ? createPortal(content, mount) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Editors                                                             */
/* ------------------------------------------------------------------ */

function LockGlyph() {
  return (
    <svg className="ft-lock__icon" viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">
      <path
        fill="currentColor"
        d="M8 1a3.5 3.5 0 0 0-3.5 3.5V7H4a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1h-.5V4.5A3.5 3.5 0 0 0 8 1Zm2 6H6V4.5a2 2 0 1 1 4 0V7Z"
      />
    </svg>
  );
}

function BubbleGlyph() {
  return (
    <svg viewBox="0 0 16 16" width="10" height="10" aria-hidden="true">
      <path fill="currentColor" d="M2 2.5A1.5 1.5 0 0 1 3.5 1h9A1.5 1.5 0 0 1 14 2.5v7a1.5 1.5 0 0 1-1.5 1.5H7l-3.2 2.8A.5.5 0 0 1 3 13.4V11h0A1.5 1.5 0 0 1 2 9.5v-7Z" />
    </svg>
  );
}

/** "+ Lägg till kommentar": an optional comment saved together with the change. */
function ChangeComment({
  label,
  value,
  onChange,
  onKeyDown,
}: {
  label: string | null;
  value: string;
  onChange: (text: string) => void;
  onKeyDown: (e: KeyboardEvent) => void;
}) {
  const [open, setOpen] = useState(false);
  if (!label) return null;
  if (!open) {
    return (
      <button type="button" className="ft-btn ft-btn--link" onClick={() => setOpen(true)}>
        + Lägg till kommentar
      </button>
    );
  }
  return (
    <div className="ft-comment">
      <label>{label}</label>
      <textarea
        autoFocus
        aria-label={label}
        rows={2}
        maxLength={4000}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
      />
    </div>
  );
}

const keyHandler = (save: () => void, cancel: () => void) => (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    e.preventDefault();
    cancel();
  } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
    e.preventDefault();
    save();
  }
};

function EditorActions({ busy, onSave, onCancel, saveLabel = 'Spara' }: { busy: boolean; onSave: () => void; onCancel: () => void; saveLabel?: string }) {
  return (
    <div className="ft-editor-actions">
      <span className="ft-editor-hint">Ctrl+Enter sparar · Esc avbryter</span>
      <button type="button" className="ft-btn" onClick={onCancel} disabled={busy}>
        Avbryt
      </button>
      <button type="button" className="ft-btn ft-btn--primary" onClick={onSave} disabled={busy}>
        {busy ? 'Sparar…' : saveLabel}
      </button>
    </div>
  );
}

/** Chip text + "Infoga fält" for one editor; owns nothing but the picker. */
function ChipTextField({
  adapt,
  initial,
  original,
  pending,
  onPending,
  onChange,
  onKeyDown,
  label,
  placeholder,
  minLines,
  autoFocus,
}: {
  adapt: AdaptApi;
  initial: string;
  /** The run being edited: chips for references the picker does not offer show their value. */
  original?: readonly InlineNode[];
  pending: readonly PendingField[];
  onPending: (next: PendingField[]) => void;
  onChange: (text: string) => void;
  onKeyDown: (e: KeyboardEvent) => void;
  label: string;
  placeholder?: string;
  minLines?: number;
  autoFocus?: boolean;
}) {
  const editor = useRef<TokenEditorHandle | null>(null);
  const [picker, setPicker] = useState(false);
  const fields = useMemo(() => pickerFields(adapt.fields, pending), [adapt.fields, pending]);
  const labels = useMemo(() => chipLabels(fields), [fields]);
  const byKey = useMemo(() => new Map<string, PickerField>(fields.map((f) => [f.key, f])), [fields]);
  const extra = useMemo(() => {
    const m = new Map<string, ChipInfo>();
    for (const v of valuesIn([...(original ?? [])])) {
      if (v.sourceKey && !m.has(v.sourceKey)) m.set(v.sourceKey, { label: v.text ?? 'tomt fält', title: 'Värde från formuläret' });
    }
    return m;
  }, [original]);
  const chip = (key: string): ChipInfo | null => {
    const f = byKey.get(key);
    if (f) return { label: labels.get(key) ?? f.label, title: chipTitle(f) };
    return extra.get(key) ?? null;
  };
  return (
    <>
      <TokenEditor
        ref={editor}
        initial={initial}
        chip={chip}
        onChange={onChange}
        onKeyDown={onKeyDown}
        label={label}
        placeholder={placeholder}
        minLines={minLines}
        autoFocus={autoFocus}
      />
      <div className="ft-chipbar">
        <button
          type="button"
          className="ft-btn ft-btn--field"
          aria-expanded={picker}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setPicker((p) => !p)}
        >
          Infoga fält
        </button>
        <span className="ft-editor-hint">Gula fält följer formuläret – skriv fritt runt dem. Tar du bort ett fält blir platsen fast text.</span>
      </div>
      {picker && (
        <FieldPicker
          fields={fields}
          labels={labels}
          onClose={() => {
            setPicker(false);
            editor.current?.focus();
          }}
          onPick={(key) => {
            setPicker(false);
            editor.current?.insertChip(key);
          }}
          onCreate={(name, type) => {
            const out = adapt.newField(name, type, pending);
            if (typeof out === 'string') return out;
            onPending([...pending, out]);
            setPicker(false);
            editor.current?.insertChip(out.key, { label: out.label, title: `${out.label}: nytt eget fält` });
            return out;
          }}
        />
      )}
    </>
  );
}

const chipTitle = (f: PickerField): string =>
  `${f.group ? `${f.group} · ` : ''}${f.label}: ${f.pending ? 'nytt eget fält – fylls i under Egna uppgifter' : (f.preview ?? 'tomt')}`;

/** Labels of dropped references, for the "kopplas loss" warning. */
function droppedLabels(adapt: AdaptApi, original: readonly InlineNode[] | undefined, before: string, after: string): string[] {
  const values = valuesIn([...(original ?? [])]);
  return droppedReferences(before, after).map((key) => {
    const f = adapt.fields.find((x) => x.key === key);
    if (f) return f.label;
    const v = values.find((x) => x.sourceKey === key);
    return v?.text ? `”${v.text}”` : 'tomt fält';
  });
}

function PendingNote({ pending, texts }: { pending: readonly PendingField[]; texts: readonly string[] }) {
  const used = usedPending(pending, ...texts);
  if (used.length === 0) return null;
  return (
    <p className="ft-note ft-note--info">
      Nya egna fält skapas när du sparar: {used.map((p) => p.label).join(', ')}. Fyll i dem under ”Egna uppgifter” i formuläret.
    </p>
  );
}

function TextEditor({
  adapt,
  initial,
  original,
  busy,
  commentLabel,
  onSave,
  onCancel,
}: {
  adapt: AdaptApi;
  initial: string;
  original: InlineNode[];
  busy: boolean;
  commentLabel: string | null;
  onSave: (text: string, pending: PendingField[], comment: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(initial);
  const [pending, setPending] = useState<PendingField[]>([]);
  const [comment, setComment] = useState('');
  const dropped = useMemo(() => droppedLabels(adapt, original, initial, text), [adapt, original, initial, text]);
  const save = () => {
    if (text === initial) onCancel();
    else if (text.trim()) onSave(text, usedPending(pending, text), comment);
  };
  return (
    <div className="ft-editor">
      <label>Redigera text</label>
      <ChipTextField
        adapt={adapt}
        initial={initial}
        original={original}
        pending={pending}
        onPending={setPending}
        onChange={setText}
        onKeyDown={keyHandler(save, onCancel)}
        label="Redigera text"
        minLines={Math.min(10, Math.max(3, Math.ceil(initial.length / 90) + 1))}
        autoFocus
      />
      {dropped.length > 0 && (
        <p className="ft-note ft-note--warn">
          Fältet kopplas loss från formuläret: {dropped.join(', ')}. Platsen följer inte längre formuläret.
        </p>
      )}
      <PendingNote pending={pending} texts={[text]} />
      <ChangeComment label={commentLabel} value={comment} onChange={setComment} onKeyDown={keyHandler(save, onCancel)} />
      <EditorActions busy={busy} onSave={save} onCancel={onCancel} />
    </div>
  );
}

function TitleEditor({
  initial,
  busy,
  commentLabel,
  onSave,
  onCancel,
}: {
  initial: string;
  busy: boolean;
  commentLabel: string | null;
  onSave: (title: string, comment: string) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initial);
  const [comment, setComment] = useState('');
  const save = () => {
    const t = title.replace(/\s+/g, ' ').trim();
    if (!t || t === initial) onCancel();
    else onSave(t, comment);
  };
  return (
    <div className="ft-editor">
      <label>Rubrik</label>
      <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={keyHandler(save, onCancel)} />
      <ChangeComment label={commentLabel} value={comment} onChange={setComment} onKeyDown={keyHandler(save, onCancel)} />
      <EditorActions busy={busy} onSave={save} onCancel={onCancel} />
    </div>
  );
}

function InsertParagraphEditor({
  adapt,
  busy,
  commentLabel,
  onSave,
  onCancel,
}: {
  adapt: AdaptApi;
  busy: boolean;
  commentLabel: string | null;
  onSave: (text: string, pending: PendingField[], comment: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState('');
  const [pending, setPending] = useState<PendingField[]>([]);
  const [comment, setComment] = useState('');
  const save = () => {
    if (text.trim()) onSave(text, usedPending(pending, text), comment);
  };
  return (
    <div className="ft-editor">
      <label>Nytt stycke</label>
      <ChipTextField
        adapt={adapt}
        initial=""
        pending={pending}
        onPending={setPending}
        onChange={setText}
        onKeyDown={keyHandler(save, onCancel)}
        label="Nytt stycke"
        placeholder="Skriv text. Infoga fält från formuläret med knappen nedan."
        autoFocus
      />
      <PendingNote pending={pending} texts={[text]} />
      <ChangeComment label={commentLabel} value={comment} onChange={setComment} onKeyDown={keyHandler(save, onCancel)} />
      <EditorActions busy={busy} onSave={save} onCancel={onCancel} saveLabel="Infoga" />
    </div>
  );
}

function OwnSectionEditor({
  adapt,
  busy,
  commentLabel,
  onSave,
  onCancel,
}: {
  adapt: AdaptApi;
  busy: boolean;
  commentLabel: string | null;
  onSave: (title: string, body: string, pending: PendingField[], comment: string) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [pending, setPending] = useState<PendingField[]>([]);
  const [comment, setComment] = useState('');
  const save = () => {
    if (title.trim()) onSave(title, body, usedPending(pending, body), comment);
  };
  return (
    <div className="ft-editor">
      <label>Nytt eget avsnitt</label>
      <input
        autoFocus
        placeholder="Rubrik, t.ex. Besiktning"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={keyHandler(save, onCancel)}
      />
      <ChipTextField
        adapt={adapt}
        initial=""
        pending={pending}
        onPending={setPending}
        onChange={setBody}
        onKeyDown={keyHandler(save, onCancel)}
        label="Avsnittets text"
        placeholder="Text. Tom rad mellan stycken. Infoga fält med knappen nedan – eller skapa ett eget fält."
        minLines={4}
      />
      <PendingNote pending={pending} texts={[body]} />
      <p className="ft-note ft-note--info">Avsnittet numreras automatiskt bland de övriga avsnitten.</p>
      <ChangeComment label={commentLabel} value={comment} onChange={setComment} onKeyDown={keyHandler(save, onCancel)} />
      <EditorActions busy={busy} onSave={save} onCancel={onCancel} saveLabel="Lägg till" />
    </div>
  );
}

function LabelEditor({
  adapt,
  initial,
  original,
  busy,
  commentLabel,
  onSave,
  onCancel,
}: {
  adapt: AdaptApi;
  initial: string;
  original: InlineNode[];
  busy: boolean;
  commentLabel: string | null;
  onSave: (text: string, pending: PendingField[], comment: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(initial);
  const [pending, setPending] = useState<PendingField[]>([]);
  const [comment, setComment] = useState('');
  const dropped = useMemo(() => droppedLabels(adapt, original, initial, text), [adapt, original, initial, text]);
  const save = () => {
    if (text === initial) onCancel();
    else if (text.trim()) onSave(text, usedPending(pending, text), comment);
  };
  return (
    <span className="ft-editor ft-editor--inline">
      <label>Redigera etikett</label>
      <ChipTextField
        adapt={adapt}
        initial={initial}
        original={original}
        pending={pending}
        onPending={setPending}
        onChange={setText}
        onKeyDown={keyHandler(save, onCancel)}
        label="Redigera etikett"
        minLines={1}
        autoFocus
      />
      {dropped.length > 0 && (
        <p className="ft-note ft-note--warn">Fältet kopplas loss från formuläret: {dropped.join(', ')}.</p>
      )}
      <PendingNote pending={pending} texts={[text]} />
      <ChangeComment label={commentLabel} value={comment} onChange={setComment} onKeyDown={keyHandler(save, onCancel)} />
      <EditorActions busy={busy} onSave={save} onCancel={onCancel} />
    </span>
  );
}

/** A detail-list row: label + value as token text (value paragraphs separated by a blank line). */
function RowEditor({
  adapt,
  initialTerm,
  initialValue,
  valueEditable,
  original,
  busy,
  saveLabel,
  commentLabel,
  onSave,
  onCancel,
}: {
  adapt: AdaptApi;
  initialTerm: string;
  initialValue: string;
  /** False when the value holds more than paragraphs (lists, tables) — only the label is editable then. */
  valueEditable: boolean;
  original: InlineNode[];
  busy: boolean;
  saveLabel: string;
  commentLabel: string | null;
  onSave: (term: string, value: string | undefined, pending: PendingField[], comment: string) => void;
  onCancel: () => void;
}) {
  const [term, setTerm] = useState(initialTerm);
  const [value, setValue] = useState(initialValue);
  const [pending, setPending] = useState<PendingField[]>([]);
  const [comment, setComment] = useState('');
  const dropped = useMemo(
    () => droppedLabels(adapt, original, `${initialTerm}\n${initialValue}`, `${term}\n${valueEditable ? value : initialValue}`),
    [adapt, original, initialTerm, initialValue, term, value, valueEditable],
  );
  const save = () => {
    if (term === initialTerm && (!valueEditable || value === initialValue)) onCancel();
    else if (term.trim() || value.trim()) onSave(term, valueEditable ? value : undefined, usedPending(pending, term, value), comment);
  };
  return (
    <div className="ft-editor">
      <label>Etikett</label>
      <ChipTextField
        adapt={adapt}
        initial={initialTerm}
        original={original}
        pending={pending}
        onPending={setPending}
        onChange={setTerm}
        onKeyDown={keyHandler(save, onCancel)}
        label="Etikett"
        placeholder="T.ex. Tillträdesdag"
        minLines={1}
        autoFocus
      />
      <label>Värde</label>
      {valueEditable ? (
        <ChipTextField
          adapt={adapt}
          initial={initialValue}
          original={original}
          pending={pending}
          onPending={setPending}
          onChange={setValue}
          onKeyDown={keyHandler(save, onCancel)}
          label="Värde"
          placeholder="Text eller fält från formuläret. Tom rad mellan stycken."
          minLines={2}
        />
      ) : (
        <p className="ft-note ft-note--info">Värdet innehåller mer än text och kan inte skrivas om här – bara etiketten.</p>
      )}
      {dropped.length > 0 && (
        <p className="ft-note ft-note--warn">
          Fältet kopplas loss från formuläret: {dropped.join(', ')}. Platsen följer inte längre formuläret.
        </p>
      )}
      <PendingNote pending={pending} texts={[term, value]} />
      <ChangeComment label={commentLabel} value={comment} onChange={setComment} onKeyDown={keyHandler(save, onCancel)} />
      <EditorActions busy={busy} onSave={save} onCancel={onCancel} saveLabel={saveLabel} />
    </div>
  );
}

/** Change classes with the edit's colour layer (`rd-ins--adaptation` / `rd-ins--suggestion`). */
function changeClass(kind: 'ins' | 'del', edit: EditMark | undefined, block = false): string {
  return [`rd-${kind}`, block ? `rd-${kind}--block` : '', edit?.layer ? `rd-${kind}--${edit.layer}` : ''].filter(Boolean).join(' ');
}

function layerProps(edit: EditMark | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (edit?.layer) out['data-layer'] = edit.layer;
  if (edit?.suggestion) out['data-suggestion'] = edit.suggestion;
  return out;
}
